import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useDictionary } from '@/shared/lib/i18n';
import { toast } from '@/shared/lib/toast';
import { QueryKey } from '@/types/query';

import { translateMessageClient } from './translate-message.client';
import type { TranslateMessageParams, TranslateMessageResponse } from './translate-message.types';

const TRANSLATION_CACHE_TIME = 1000 * 60 * 30;

export const getTranslateMessageCacheKey = ({ message, targetLanguage, subject }: TranslateMessageParams) => [
  QueryKey.useTranslateMessageMutation,
  { message, targetLanguage, subject: subject ?? null },
];

export const useTranslateMessageMutation = () => {
  const dictionary = useDictionary();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ message, targetLanguage, subject }: TranslateMessageParams): Promise<TranslateMessageResponse> => {
      // The same content translated to the same language always yields the same result, so it is
      // served from the cache instead of hitting the endpoint again.
      return queryClient.fetchQuery({
        queryKey: getTranslateMessageCacheKey({ message, targetLanguage, subject }),
        queryFn: async () => {
          const response = await translateMessageClient.translateMessage({
            message,
            language: targetLanguage,
            subject,
          });

          return {
            translatedMessage: response.data.translated_message,
            originalMessage: response.data.original_message,
            targetLanguage: response.data.target_language,
            translatedSubject: response.data.translated_subject,
            originalSubject: response.data.original_subject,
          };
        },
        cacheTime: TRANSLATION_CACHE_TIME,
      });
    },
    {
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : dictionary.somethingWentWrong, { duration: '10s' });
      },
      onSuccess: () => {
        toast.success(dictionary.messageTranslatedSuccessfully);
      },
    },
  );
};
